import { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, Area, AreaChart,
} from 'recharts';
import { api } from '../api';

const COLORS_STATUS = {
  paid:      { color: '#4ade80', label: 'Payées' },
  pending:   { color: '#fbbf24', label: 'En attente' },
  draft:     { color: '#94a3b8', label: 'Brouillons' },
  cancelled: { color: '#f87171', label: 'Annulées' },
  overdue:   { color: '#ef4444', label: 'En retard' },
};

const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const pct = (n) => `${n || 0} %`;

const KPI = ({ label, value, sub, color = '#3b82f6', icon }) => (
  <div style={{ background: 'rgba(30,41,59,0.8)', border: `1px solid ${color}33`, borderRadius: '0.75rem', padding: '1.25rem 1.5rem', flex: '1 1 200px', minWidth: 0 }}>
    <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>{icon}</div>
    <div style={{ fontSize: '1.6rem', fontWeight: '800', color }}>{value}</div>
    <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginTop: '0.25rem' }}>{label}</div>
    {sub && <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '0.2rem' }}>{sub}</div>}
  </div>
);

const CustomTooltip = ({ active, payload, label, currency }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '0.5rem', padding: '0.75rem 1rem' }}>
      <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '0.4rem' }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, fontWeight: '600', fontSize: '0.9rem' }}>
          {p.name} : {currency ? fmt(p.value) : p.value}
        </div>
      ))}
    </div>
  );
};

// Composant modal plein écran pour agrandir un graphique sur mobile
const ChartModal = ({ title, children, onClose }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem',
    }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        background: '#0f172a', border: '1px solid #334155',
        borderRadius: '1rem', padding: '1.5rem',
        width: '100%', maxWidth: '600px',
        maxHeight: '90dvh', overflowY: 'auto',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: '700', color: '#e2e8f0', margin: 0 }}>{title}</h3>
        <button
          onClick={onClose}
          style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#e2e8f0', borderRadius: '50%', width: '2rem', height: '2rem', cursor: 'pointer', fontSize: '1rem', lineHeight: 1 }}
        >✕</button>
      </div>
      {children}
    </div>
  </div>
);

export default function Analytique() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overdue, setOverdue] = useState([]);
  const [expanded, setExpanded] = useState(null); // 'ca' | 'pie' | 'clients' | 'depenses'

  useEffect(() => {
    Promise.all([api.getAnalytics(), api.getOverdueInvoices()])
      .then(([analytics, overdueRes]) => {
        setData(analytics);
        setOverdue(overdueRes.invoices || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#94a3b8' }}>
      ⏳ Chargement des statistiques...
    </div>
  );

  if (!data) return (
    <div style={{ color: '#f87171', padding: '2rem' }}>Erreur lors du chargement des stats.</div>
  );

  const { byStatus = {}, overdue: overdueStats = {}, recoveryRate = 0, monthly = [], topClients = [], depensesMonthly = [], quotesStats = [] } = data;

  const totalCA = Object.values(byStatus).reduce((a, v) => a + v.total, 0);
  const paidTotal = byStatus.paid?.total || 0;
  const pendingTotal = byStatus.pending?.total || 0;
  const overdueTotal = overdueStats.total || 0;

  // Pie data for invoice status
  const pieData = Object.entries(byStatus)
    .filter(([, v]) => v.count > 0)
    .map(([k, v]) => ({
      name: COLORS_STATUS[k]?.label || k,
      value: v.count,
      color: COLORS_STATUS[k]?.color || '#64748b',
    }));
  if (overdueStats.count > 0) {
    pieData.push({ name: 'En retard', value: overdueStats.count, color: '#ef4444' });
  }

  // Quotes conversion data
  const quoteConv = {};
  quotesStats.forEach(q => { quoteConv[q.status] = q.count; });
  const totalQuotes = Object.values(quoteConv).reduce((a, c) => a + parseInt(c), 0);
  const acceptedQuotes = parseInt(quoteConv.accepted || quoteConv.acceptée || 0) + parseInt(quoteConv.converted || 0);
  const convRate = totalQuotes > 0 ? Math.round((acceptedQuotes / totalQuotes) * 100) : 0;

  return (
    <div style={{ color: '#e2e8f0', maxWidth: '1200px' }}>
      <h2 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.5rem', color: 'white' }}>
        📊 Tableau de bord analytique
      </h2>
      <p style={{ color: '#64748b', marginBottom: '2rem', fontSize: '0.9rem' }}>
        Vue d'ensemble de votre activité — données en temps réel
      </p>

      {/* KPI Row */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
        <KPI icon="💶" label="CA total facturé" value={fmt(totalCA)} color="#3b82f6" />
        <KPI icon="✅" label="CA encaissé" value={fmt(paidTotal)} color="#4ade80" />
        <KPI icon="⏳" label="En attente" value={fmt(pendingTotal)} color="#fbbf24" />
        <KPI icon="⚠️" label="En retard" value={fmt(overdueTotal)} sub={`${overdueStats.count || 0} facture(s)`} color="#ef4444" />
        <KPI icon="📈" label="Taux recouvrement" value={pct(recoveryRate)} color="#a78bfa" />
        <KPI icon="🤝" label="Taux conversion devis" value={pct(convRate)} sub={`${acceptedQuotes}/${totalQuotes} devis`} color="#38bdf8" />
      </div>

      {/* Charts row 1: CA mensuel + Répartition statuts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* CA Mensuel */}
        <div
          onClick={() => setExpanded('ca')}
          style={{ background: 'rgba(30,41,59,0.8)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.5rem', cursor: 'pointer', position: 'relative' }}
        >
          <div style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', fontSize: '0.7rem', color: '#64748b' }}>⤢ agrandir</div>
          <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#e2e8f0' }}>
            💹 CA mensuel — 12 derniers mois
          </h3>
          {monthly.length === 0 ? (
            <div style={{ color: '#64748b', textAlign: 'center', padding: '2rem' }}>Aucune donnée</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={monthly}>
                <defs>
                  <linearGradient id="gradTotal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradPaid" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4ade80" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#4ade80" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 11 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <Tooltip content={<CustomTooltip currency />} />
                <Legend wrapperStyle={{ fontSize: '0.8rem', color: '#94a3b8' }} />
                <Area type="monotone" dataKey="total" name="Facturé" stroke="#3b82f6" fill="url(#gradTotal)" strokeWidth={2} />
                <Area type="monotone" dataKey="paid" name="Encaissé" stroke="#4ade80" fill="url(#gradPaid)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Répartition statuts */}
        <div
          onClick={() => setExpanded('pie')}
          style={{ background: 'rgba(30,41,59,0.8)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.5rem', cursor: 'pointer', position: 'relative' }}
        >
          <div style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', fontSize: '0.7rem', color: '#64748b' }}>⤢ agrandir</div>
          <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#e2e8f0' }}>
            🥧 Répartition factures par statut
          </h3>
          {pieData.length === 0 ? (
            <div style={{ color: '#64748b', textAlign: 'center', padding: '2rem' }}>Aucune donnée</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value">
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(val, name) => [val + ' facture(s)', name]} contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '0.5rem' }} />
                <Legend wrapperStyle={{ fontSize: '0.8rem', color: '#94a3b8' }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Charts row 2: Top clients + Dépenses */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Top clients */}
        <div
          onClick={() => setExpanded('clients')}
          style={{ background: 'rgba(30,41,59,0.8)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.5rem', cursor: 'pointer', position: 'relative' }}
        >
          <div style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', fontSize: '0.7rem', color: '#64748b' }}>⤢ agrandir</div>
          <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#e2e8f0' }}>
            🏆 Top 5 clients
          </h3>
          {topClients.length === 0 ? (
            <div style={{ color: '#64748b', textAlign: 'center', padding: '2rem' }}>Aucune donnée</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={topClients} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
                <XAxis type="number" tick={{ fill: '#64748b', fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)}k€`} />
                <YAxis type="category" dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} width={100} />
                <Tooltip content={<CustomTooltip currency />} />
                <Bar dataKey="total" name="CA" fill="#3b82f6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Dépenses mensuelles */}
        <div
          onClick={() => setExpanded('depenses')}
          style={{ background: 'rgba(30,41,59,0.8)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.5rem', cursor: 'pointer', position: 'relative' }}
        >
          <div style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', fontSize: '0.7rem', color: '#64748b' }}>⤢ agrandir</div>
          <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#e2e8f0' }}>
            💸 Dépenses mensuelles
          </h3>
          {depensesMonthly.length === 0 ? (
            <div style={{ color: '#64748b', textAlign: 'center', padding: '2rem' }}>Aucune dépense enregistrée</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={depensesMonthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 11 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <Tooltip content={<CustomTooltip currency />} />
                <Bar dataKey="total" name="Dépenses" fill="#f87171" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Modals plein écran graphiques */}
      {expanded === 'ca' && (
        <ChartModal title="💹 CA mensuel — 12 derniers mois" onClose={() => setExpanded(null)}>
          <ResponsiveContainer width="100%" height={340}>
            <AreaChart data={monthly}>
              <defs>
                <linearGradient id="gradTotalM" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradPaidM" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4ade80" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#4ade80" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 11 }} />
              <YAxis tick={{ fill: '#64748b', fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
              <Tooltip content={<CustomTooltip currency />} />
              <Legend wrapperStyle={{ fontSize: '0.85rem', color: '#94a3b8' }} />
              <Area type="monotone" dataKey="total" name="Facturé" stroke="#3b82f6" fill="url(#gradTotalM)" strokeWidth={2} />
              <Area type="monotone" dataKey="paid" name="Encaissé" stroke="#4ade80" fill="url(#gradPaidM)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartModal>
      )}
      {expanded === 'pie' && (
        <ChartModal title="🥧 Répartition factures par statut" onClose={() => setExpanded(null)}>
          <ResponsiveContainer width="100%" height={340}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="45%" innerRadius={80} outerRadius={130} paddingAngle={3} dataKey="value">
                {pieData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(val, name) => [val + ' facture(s)', name]} contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '0.5rem' }} />
              <Legend wrapperStyle={{ fontSize: '0.85rem', color: '#94a3b8' }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartModal>
      )}
      {expanded === 'clients' && (
        <ChartModal title="🏆 Top 5 clients" onClose={() => setExpanded(null)}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topClients} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
              <XAxis type="number" tick={{ fill: '#64748b', fontSize: 12 }} tickFormatter={(v) => `${Math.round(v / 1000)}k€`} />
              <YAxis type="category" dataKey="name" tick={{ fill: '#94a3b8', fontSize: 12 }} width={110} />
              <Tooltip content={<CustomTooltip currency />} />
              <Bar dataKey="total" name="CA" fill="#3b82f6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartModal>
      )}
      {expanded === 'depenses' && (
        <ChartModal title="💸 Dépenses mensuelles" onClose={() => setExpanded(null)}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={depensesMonthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 12 }} />
              <YAxis tick={{ fill: '#64748b', fontSize: 12 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
              <Tooltip content={<CustomTooltip currency />} />
              <Bar dataKey="total" name="Dépenses" fill="#f87171" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartModal>
      )}

      {/* Factures en retard */}
      {overdue.length > 0 && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '0.75rem', padding: '1.5rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#f87171' }}>
            ⚠️ {overdue.length} facture(s) en retard de paiement
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(239,68,68,0.2)' }}>
                  {['Numéro', 'Client', 'Montant', 'Échéance', 'Retard'].map(h => (
                    <th key={h} style={{ padding: '0.5rem 0.75rem', textAlign: 'left', color: '#94a3b8', fontWeight: '600', fontSize: '0.78rem', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {overdue.map((inv) => {
                  const daysLate = Math.floor((Date.now() - new Date(inv.due_date).getTime()) / 86400000);
                  return (
                    <tr key={inv.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <td style={{ padding: '0.6rem 0.75rem', color: '#e2e8f0', fontWeight: '600' }}>{inv.number}</td>
                      <td style={{ padding: '0.6rem 0.75rem', color: '#94a3b8' }}>{inv.client_name}</td>
                      <td style={{ padding: '0.6rem 0.75rem', color: '#f87171', fontWeight: '700' }}>{fmt(inv.amount)}</td>
                      <td style={{ padding: '0.6rem 0.75rem', color: '#f87171' }}>{new Date(inv.due_date).toLocaleDateString('fr-FR')}</td>
                      <td style={{ padding: '0.6rem 0.75rem' }}>
                        <span style={{ background: daysLate > 14 ? 'rgba(239,68,68,0.2)' : 'rgba(251,191,36,0.2)', color: daysLate > 14 ? '#f87171' : '#fbbf24', borderRadius: '1rem', padding: '0.2rem 0.6rem', fontSize: '0.78rem', fontWeight: '700' }}>
                          {daysLate} j
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
